// Design System for Project Management Components
// Centralized styling constants for consistent UI across all tabs and components

export const DesignSystem = {
  // Typography
  typography: {
    // Table Headers
    tableHeader: {
      color: '#70707D',
      fontSize: '12px',
      fontFamily: '"Arcadia Text", system-ui, sans-serif',
      fontWeight: 400,
      padding: '8px',
    },
    
    // Form Labels (smaller padding for expanded task view)
    formLabel: {
      color: '#70707D',
      fontSize: '12px',
      fontFamily: '"Arcadia Text", system-ui, sans-serif',
      fontWeight: 400,
      padding: '2px 0px',
    },
    
    // Task Names
    taskName: {
      color: '#363644',
      fontSize: '15px',
      fontFamily: '"Arcadia Text", system-ui, sans-serif',
      fontWeight: 360,
      margin: '0px',
    },
    
    // Meeting Notes
    meetingNotes: {
      color: '#363644',
      fontSize: '15px',
      fontFamily: '"Arcadia Text", system-ui, sans-serif',
      fontWeight: 360,
      margin: '0px',
    },
    
    // Multi-line Text Fields (Daily Updates, Meeting Notes, etc.)
    multilineField: {
      fontSize: '13px',
      fontFamily: '"Arcadia Text", system-ui, sans-serif',
      color: '#374151',
      lineHeight: '1.3'
    },
    
    // General Text
    body: {
      fontFamily: '"Arcadia Text", system-ui, sans-serif',
      fontSize: '14px',
      color: '#363644',
    },
    
    // Small Text
    small: {
      fontFamily: '"Arcadia Text", system-ui, sans-serif',
      fontSize: '12px',
      color: '#70707D',
    },
  },

  // Colors
  colors: {
    // Primary Colors
    primary: {
      text: '#363644',
      textSecondary: '#70707D',
      textMuted: '#9CA3AF',
    },
    
    // Brand Colors - Full Indigo Palette (Main Brand Color)
    brand: {
      50: '#eef2ff',           // Very light backgrounds, subtle highlights
      100: '#e0e7ff',          // Light backgrounds, disabled states
      200: '#c7d2fe',          // Light borders, subtle elements
      300: '#a5b4fc',          // Inactive/disabled elements
      400: '#818cf8',          // Secondary elements, muted states
      500: '#6366f1',          // Alternative primary
      600: '#4f46e5',          // PRIMARY BRAND COLOR ⭐
      700: '#4338ca',          // Hover states, pressed buttons
      800: '#3730a3',          // Active states, focused elements
      900: '#312e81',          // Dark text on light backgrounds
      950: '#1e1b4b',          // Very dark text, high contrast
    },
    
    // UNIFIED SEMANTIC COLORS - Consistent across all components
    semantic: {
      // Risk Level Colors (based on map colors user likes)
      risk: {
        critical: '#991b1b',    // red-800 - Critical risk (darker red)
        high: '#ef4444',        // red-500 - High risk
        medium: '#eab308',      // yellow-500 - Medium risk  
        low: '#22c55e',         // green-500 - Low risk
        none: '#6b7280',        // gray-500 - No risk
      },
      
      // Status Colors
      success: '#22c55e',       // green-500 - Success actions
      warning: '#eab308',       // yellow-500 - Warning actions
      error: '#ef4444',         // red-500 - Error actions
      info: '#3b82f6',          // blue-500 - Info actions
      primary: '#4f46e5',       // indigo-600 - Primary brand
      
      // Issue/Priority Colors (for consistency)
      critical: '#ef4444',      // red-500 - Critical issues
      high: '#f97316',          // orange-500 - High priority
      medium: '#eab308',        // yellow-500 - Medium priority
      low: '#22c55e',           // green-500 - Low priority
      
      // Background variants of semantic colors
      backgrounds: {
        risk: {
          high: '#fef2f2',      // red-50
          medium: '#fefce8',    // yellow-50
          low: '#f0fdf4',       // green-50
          none: '#f9fafb',      // gray-50
        },
        success: '#f0fdf4',     // green-50
        warning: '#fefce8',     // yellow-50
        error: '#fef2f2',       // red-50
        info: '#eff6ff',        // blue-50
        primary: '#eef2ff',     // indigo-50
      },
      
      // Text variants of semantic colors  
      text: {
        risk: {
          high: '#dc2626',      // red-600
          medium: '#ca8a04',    // yellow-600
          low: '#16a34a',       // green-600
          none: '#4b5563',      // gray-600
        },
        success: '#16a34a',     // green-600
        warning: '#ca8a04',     // yellow-600
        error: '#dc2626',       // red-600
        info: '#2563eb',        // blue-600
        primary: '#4338ca',     // indigo-700
      },
      
      // Border variants of semantic colors
      borders: {
        risk: {
          high: '#fecaca',      // red-200
          medium: '#fef08a',    // yellow-200
          low: '#bbf7d0',       // green-200
          none: '#e5e7eb',      // gray-200
        },
        success: '#bbf7d0',     // green-200
        warning: '#fef08a',     // yellow-200
        error: '#fecaca',       // red-200
        info: '#dbeafe',        // blue-200
        primary: '#c7d2fe',     // indigo-200
      },
    },
    
    // Gray Palette - Full Spectrum
    gray: {
      50: '#f9fafb',           // Very light backgrounds
      100: '#f3f4f6',          // Light backgrounds, table stripes
      200: '#e5e7eb',          // Light borders, dividers
      300: '#d1d5db',          // Default borders, disabled text
      400: '#9ca3af',          // Placeholder text, icons
      500: '#6b7280',          // Secondary text
      600: '#4b5563',          // Primary text alternative
      700: '#374151',          // Primary text
      800: '#1f2937',          // Dark text, headings
      900: '#111827',          // Very dark text
      950: '#030712',          // Almost black
    },
    
    // Success Colors - Emerald Palette
    success: {
      50: '#ecfdf5',           // Light success backgrounds
      100: '#d1fae5',          // Success notifications
      200: '#a7f3d0',          // Light success borders
      300: '#6ee7b7',          // Success highlights
      400: '#34d399',          // Success buttons secondary
      500: '#10b981',          // SUCCESS PRIMARY ✅
      600: '#059669',          // Success buttons primary
      700: '#047857',          // Success hover states
      800: '#065f46',          // Success active states
      900: '#064e3b',          // Dark success text
      950: '#022c22',          // Very dark success
    },
    
    // Error/Danger Colors - Red Palette
    error: {
      50: '#fef2f2',           // Light error backgrounds
      100: '#fee2e2',          // Error notifications
      200: '#fecaca',          // Light error borders
      300: '#fca5a5',          // Error highlights
      400: '#f87171',          // Error buttons secondary
      500: '#ef4444',          // ERROR PRIMARY ❌
      600: '#dc2626',          // Error buttons primary
      700: '#b91c1c',          // Error hover states
      800: '#991b1b',          // Error active states
      900: '#7f1d1d',          // Dark error text
      950: '#450a0a',          // Very dark error
    },
    
    // Warning Colors - Amber Palette
    warning: {
      50: '#fffbeb',           // Light warning backgrounds
      100: '#fef3c7',          // Warning notifications
      200: '#fde68a',          // Light warning borders
      300: '#fcd34d',          // Warning highlights
      400: '#fbbf24',          // Warning buttons secondary
      500: '#f59e0b',          // WARNING PRIMARY ⚠️
      600: '#d97706',          // Warning buttons primary
      700: '#b45309',          // Warning hover states
      800: '#92400e',          // Warning active states
      900: '#78350f',          // Dark warning text
      950: '#451a03',          // Very dark warning
    },
    
    // Legacy/Backward Compatibility
          link: {
      primary: '#4f46e5',      // brand.600
      hover: '#4338ca',        // brand.700
      light: '#eef2ff',        // brand.50
      muted: '#818cf8',        // brand.400
      },
    
    risk: {
      high: '#ef4444',         // error.500
      medium: '#f59e0b',       // warning.500
      low: '#10b981',          // success.500
      none: '#d1d5db',         // gray.300
    },
    
    status: {
      completed: '#10b981',    // success.500
      inProgress: '#4f46e5',   // brand.600 (updated!)
      onHold: '#f59e0b',       // warning.500
      notStarted: '#9ca3af',   // gray.400
    },
    
    background: {
      white: '#ffffff',        // Pure white
      gray50: '#f9fafb',       // gray.50
      gray100: '#f3f4f6',      // gray.100
      brand50: '#eef2ff',      // brand.50
    },
    
    border: {
      gray100: '#f3f4f6',      // gray.100
      gray200: '#e5e7eb',      // gray.200
      gray300: '#d1d5db',      // gray.300
    },
  },

  // Spacing
  spacing: {
    // Padding
    padding: {
      xs: '4px',
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '24px',
    },
    
    // Margins
    margin: {
      xs: '4px',
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '24px',
    },
    
    // Table specific spacing
    table: {
      headerPadding: '8px',
      cellPadding: '12px 24px',
      rowSpacing: '16px',
    },
    
    // Expanded form spacing
    expandedForm: {
      containerPadding: '16px',
      columnGap: '16px',
      fieldSpacing: '16px',
    },
  },

  // Component Sizes
  sizes: {
    // Avatar sizes
    avatar: {
      xs: 16,
      sm: 20,
      md: 24,
      lg: 32,
    },
    
    // Icon sizes
    icon: {
      xs: 12,
      sm: 16,
      md: 20,
      lg: 24,
    },
    
    // Input heights
    input: {
      sm: 32,
      md: 40,
      lg: 48,
    },
  },

  // Animation
  animation: {
    // Transitions
    transition: {
      fast: '150ms ease',
      normal: '200ms ease',
      slow: '300ms ease',
    },
    
    // Hover effects
    hover: {
      scale: 'scale(1.02)',
      opacity: 'opacity-80',
    },
    
    // Loading animations
    loading: {
      spin: 'spin 3s linear infinite',
      pulse: 'pulse 2s ease-in-out infinite',
    },
  },

  // Z-Index Manager - Centralized layer management
  zIndex: {
    // Base layers (0-9)
    base: 0,                    // Default/base content
    behind: -1,                 // Elements that should be behind base content
    
    // Content layers (10-99)
    content: 10,                // Regular content
    raised: 20,                 // Slightly elevated content
    table: 50,                  // Table content (below sticky headers)
    
    // UI layers (100-999)
    dropdown: 100,              // Dropdowns, select menus
    sticky: 200,                // Sticky headers, navigation
    fixed: 300,                 // Fixed positioned elements
    sidebar: 400,               // Sidebars, drawers
    header: 500,                // Main navigation header
    
    // Overlay layers (1000-9999)
    overlay: 1000,              // Background overlays
    popover: 2000,              // Popovers, tooltips
    modal: 5000,                // Standard modals
    notification: 8000,         // Toast notifications, alerts
    
    // Critical layers (10000+)
    modalCritical: 50000,       // Critical modals (document upload, etc.)
    systemModal: 100000,        // System-level modals
    emergency: 999999,          // Emergency overlays (errors, maintenance)
    
    // Maximum priority (reserved for extreme cases)
    maximum: 2147483647,        // Maximum 32-bit integer (CreateRoomModal level)
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },

  // Border Radius
  borderRadius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    full: '9999px',
  },

  // Badges
  badges: {
    // Development badge (orange)
    dev: {
      background: '#fef3c7',    // amber-100
      text: '#d97706',          // amber-600
      border: '#fde68a',        // amber-200
    },
    // Need Review badge (yellow)
    needReview: {
      background: '#fefce8',    // yellow-50
      text: '#ca8a04',          // yellow-600
      border: '#fef08a',        // yellow-200
    },
    // In Progress badge (blue)
    inProgress: {
      background: '#eff6ff',    // blue-50
      text: '#2563eb',          // blue-600
      border: '#dbeafe',        // blue-200
    },
    // Completed badge (green)
    completed: {
      background: '#f0fdf4',    // green-50
      text: '#16a34a',          // green-600
      border: '#bbf7d0',        // green-200
    },
    // Error badge (red)
    error: {
      background: '#fef2f2',    // red-50
      text: '#dc2626',          // red-600
      border: '#fecaca',        // red-200
    },
  },
};

// Helper functions for common style combinations
export const getTableHeaderStyle = () => ({
  color: DesignSystem.colors.primary.textSecondary,
  fontSize: DesignSystem.typography.tableHeader.fontSize,
  fontFamily: DesignSystem.typography.tableHeader.fontFamily,
  fontWeight: DesignSystem.typography.tableHeader.fontWeight,
  padding: DesignSystem.typography.tableHeader.padding,
});

export const getFormLabelStyle = () => ({
  color: DesignSystem.colors.primary.textSecondary,
  fontSize: DesignSystem.typography.formLabel.fontSize,
  fontFamily: DesignSystem.typography.formLabel.fontFamily,
  fontWeight: DesignSystem.typography.formLabel.fontWeight,
  padding: DesignSystem.typography.formLabel.padding,
});

export const getTaskNameStyle = () => ({
  color: DesignSystem.colors.primary.text,
  fontSize: DesignSystem.typography.taskName.fontSize,
  fontFamily: DesignSystem.typography.taskName.fontFamily,
  fontWeight: DesignSystem.typography.taskName.fontWeight,
  margin: DesignSystem.typography.taskName.margin,
});

export const getMeetingNotesStyle = () => ({
  color: DesignSystem.colors.primary.text,
  fontSize: DesignSystem.typography.meetingNotes.fontSize,
  fontFamily: DesignSystem.typography.meetingNotes.fontFamily,
  fontWeight: DesignSystem.typography.meetingNotes.fontWeight,
  margin: DesignSystem.typography.meetingNotes.margin,
});

export const getMultilineFieldStyle = () => ({
  fontSize: DesignSystem.typography.multilineField.fontSize,
  fontFamily: DesignSystem.typography.multilineField.fontFamily,
  color: DesignSystem.typography.multilineField.color,
  lineHeight: DesignSystem.typography.multilineField.lineHeight,
});

export const getRiskColor = (riskLevel: string) => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return DesignSystem.colors.semantic.risk.critical;
    case 'high':
      return DesignSystem.colors.semantic.risk.high;
    case 'medium':
      return DesignSystem.colors.semantic.risk.medium;
    case 'low':
      return DesignSystem.colors.semantic.risk.low;
    default:
      return DesignSystem.colors.semantic.risk.none;
  }
};

export const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return DesignSystem.colors.semantic.success;
    case 'in_progress':
      return DesignSystem.colors.semantic.primary;
    case 'on_hold':
      return DesignSystem.colors.semantic.warning;
    default:
      return DesignSystem.colors.gray[500];
  }
};

// NEW UNIFIED COLOR FUNCTIONS FOR CONSISTENCY

// Risk color utilities
export const getRiskColorSet = (riskLevel: string) => {
  const level = riskLevel?.toLowerCase();
  return {
    background: DesignSystem.colors.semantic.risk[level as keyof typeof DesignSystem.colors.semantic.risk] || DesignSystem.colors.semantic.risk.none,
    text: DesignSystem.colors.semantic.text.risk[level as keyof typeof DesignSystem.colors.semantic.text.risk] || DesignSystem.colors.semantic.text.risk.none,
    border: DesignSystem.colors.semantic.borders.risk[level as keyof typeof DesignSystem.colors.semantic.borders.risk] || DesignSystem.colors.semantic.borders.risk.none,
    backgroundLight: DesignSystem.colors.semantic.backgrounds.risk[level as keyof typeof DesignSystem.colors.semantic.backgrounds.risk] || DesignSystem.colors.semantic.backgrounds.risk.none,
  };
};

// Tailwind class generators for consistent usage
export const getRiskTailwindClasses = (riskLevel: string) => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
      return {
        bg: 'bg-red-800',
        text: 'text-red-800',
        border: 'border-red-300',
        bgLight: 'bg-red-100',
      };
    case 'high':
      return {
        bg: 'bg-red-500',
        text: 'text-red-600',
        border: 'border-red-200',
        bgLight: 'bg-red-50',
      };
    case 'medium':
      return {
        bg: 'bg-yellow-500',
        text: 'text-yellow-600',
        border: 'border-yellow-200',
        bgLight: 'bg-yellow-50',
      };
    case 'low':
      return {
        bg: 'bg-green-500',
        text: 'text-green-600',
        border: 'border-green-200',
        bgLight: 'bg-green-50',
      };
    default:
      return {
        bg: 'bg-gray-500',
        text: 'text-gray-600',
        border: 'border-gray-200',
        bgLight: 'bg-gray-50',
      };
  }
};

// Status color utilities
export const getStatusTailwindClasses = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return {
        bg: 'bg-green-500',
        text: 'text-green-600',
        border: 'border-green-200',
        bgLight: 'bg-green-50',
      };
    case 'in_progress':
      return {
        bg: 'bg-indigo-600',
        text: 'text-indigo-700',
        border: 'border-indigo-200',
        bgLight: 'bg-indigo-50',
      };
    case 'on_hold':
      return {
        bg: 'bg-yellow-500',
        text: 'text-yellow-600',
        border: 'border-yellow-200',
        bgLight: 'bg-yellow-50',
      };
    default:
      return {
        bg: 'bg-gray-500',
        text: 'text-gray-600',
        border: 'border-gray-200',
        bgLight: 'bg-gray-50',
      };
  }
};

// Issue count color utilities (for MapPopup and similar components)
export const getIssueCountTailwindClasses = (count: number) => {
  if (count >= 4) {
    return {
      bg: 'bg-red-500',
      text: 'text-red-600',
      border: 'border-red-200',
      bgLight: 'bg-red-50',
    };
  } else if (count >= 2) {
    return {
      bg: 'bg-orange-500',
      text: 'text-orange-600',
      border: 'border-orange-200',
      bgLight: 'bg-orange-50',
    };
  } else if (count >= 1) {
    return {
      bg: 'bg-yellow-500',
      text: 'text-yellow-600',
      border: 'border-yellow-200',
      bgLight: 'bg-yellow-50',
    };
  } else {
    return {
      bg: 'bg-green-500',
      text: 'text-green-600',
      border: 'border-green-200',
      bgLight: 'bg-green-50',
    };
  }
};

// Legacy function for backward compatibility
export const getOriginalStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return DesignSystem.colors.semantic.success;
    case 'in_progress':
      return DesignSystem.colors.semantic.primary;
    case 'on_hold':
      return DesignSystem.colors.semantic.warning;
    default:
      return DesignSystem.colors.gray[500];
  }
};

// CSS Classes for common patterns
export const cssClasses = {
  // Table styles
  tableHeader: 'text-xs font-normal border-b border-gray-200',
  tableCell: 'px-6 py-4 text-sm',
  tableRow: 'hover:bg-gray-50 transition-colors',
  
  // Button styles
  primaryButton: 'bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors',
  secondaryButton: 'bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors',
  
  // Input styles
  textInput: 'w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
  selectInput: 'w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
  
  // Layout styles
  card: 'bg-white rounded-lg shadow-sm border border-gray-200',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  
  // Link styles
  linkPrimary: 'text-blue-600 hover:text-blue-700 transition-colors',
  linkMuted: 'text-blue-400 hover:text-blue-600 transition-colors',
};

// Compact Modal System - Reusable styles for mobile-first modal design
export const compactModal = {
  // Modal overlay/backdrop
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: '#ffffff',
    opacity: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50000, // High z-index for modals
    padding: '8px',
    boxSizing: 'border-box' as const
  },

  // Close button (top-right)
  closeButton: {
    position: 'fixed' as const,
    top: '8px',
    right: '8px',
    zIndex: 60000,
    color: '#9CA3AF',
    padding: '4px',
    background: 'none',
    border: 'none',
    cursor: 'pointer'
  },

  // Main modal container
  container: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    width: '500px', // Fixed width instead of 100%
    maxWidth: '95vw', // Prevent overflow on very small screens
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    border: '1px solid #E5E7EB',
    boxSizing: 'border-box' as const,
    flexShrink: 0 // Prevent flexbox from shrinking
  },

  // Modal header
  header: {
    marginBottom: '12px'
  },

  // Modal title
  title: {
    fontSize: '18px',
    fontWeight: '600',
    fontFamily: 'Arcadia_Display, system-ui, sans-serif',
    color: '#111827',
    margin: 0,
    lineHeight: '1.3'
  },

  // Form container
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
  },

  // Form field container
  field: {
    display: 'flex',
    flexDirection: 'column' as const
  },

  // Form labels
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif'
  },

  // Text inputs
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif',
    outline: 'none'
  },

  // Textarea inputs
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif',
    resize: 'none' as const,
    outline: 'none'
  },

  // Grid layout for side-by-side fields (like dates)
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px'
  },

  // Buttons container
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px'
  },

  // Cancel button
  cancelButton: {
    padding: '8px 16px',
    color: '#374151',
    backgroundColor: '#F3F4F6',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif',
    fontSize: '14px'
  },

  // Primary button
  primaryButton: {
    padding: '8px 16px',
    backgroundColor: '#4F46E5',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  // Disabled button state
  primaryButtonDisabled: {
    padding: '8px 16px',
    backgroundColor: '#9CA3AF',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'not-allowed',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  // Small icons (for buttons and UI elements)
  smallIcon: {
    width: '12px',
    height: '12px'
  },

  // Medium icons (for inputs)
  mediumIcon: {
    width: '14px',
    height: '14px'
  },

  // Large icons (for close button)
  largeIcon: {
    width: '20px',
    height: '20px'
  },

  // Address field container (needs relative positioning for dropdown)
  addressContainer: {
    position: 'relative' as const
  },

  // Address search dropdown
  addressDropdown: {
    position: 'absolute' as const,
    zIndex: 10,
    width: '100%',
    marginTop: '4px',
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    maxHeight: '300px', // Increased to 300px as requested
    overflowY: 'auto' as const,
    left: 0,
    right: 0
  },

  // Address suggestion item
  addressItem: {
    width: '100%',
    padding: '8px 12px',
    textAlign: 'left' as const,
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '1px solid #F3F4F6',
    cursor: 'pointer',
    fontSize: '14px'
  },

  // Address item text styles
  addressMainText: {
    fontWeight: '500',
    color: '#111827',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif'
  },

  addressSecondaryText: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '2px',
    fontFamily: 'Arcadia_Text, system-ui, sans-serif'
  }
};

// Link styles helper
export const getLinkStyle = (variant: 'primary' | 'muted' = 'primary') => ({
  color: variant === 'primary' ? DesignSystem.colors.link.primary : DesignSystem.colors.link.muted,
  textDecoration: 'none',
  transition: DesignSystem.animation.transition.normal,
  '&:hover': {
    color: DesignSystem.colors.link.hover,
  },
});

// Button styles helper for primary buttons
export const getPrimaryButtonStyle = () => ({
  backgroundColor: DesignSystem.colors.link.primary,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: DesignSystem.borderRadius.lg,
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: DesignSystem.animation.transition.normal,
  '&:hover': {
    backgroundColor: DesignSystem.colors.link.hover,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

// Select styles helpers for proper dropdown arrow positioning
export const getFormSelectContainerStyle = () => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  color: DesignSystem.colors.primary.text,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${DesignSystem.colors.border.gray200}`,
  borderRadius: DesignSystem.borderRadius.lg,
  padding: '8px 12px',
});

export const getFormSelectStyle = () => ({
  width: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  color: DesignSystem.colors.primary.text,
  cursor: 'pointer',
  paddingRight: '40px', // Space for dropdown arrow
});

export const getInlineSelectStyle = (size: 'sm' | 'md' = 'md') => ({
  width: '100%',
  fontSize: size === 'sm' ? '12px' : '14px',
  fontWeight: size === 'sm' ? 500 : 400,
  backgroundColor: 'transparent',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  paddingRight: size === 'sm' ? '32px' : '40px', // Space for dropdown arrow
  minWidth: 0,
  flex: 1,
});

export const getLocationSelectStyle = () => ({
  width: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  paddingRight: '40px', // Space for dropdown arrow
  cursor: 'pointer',
}); 

// Original Status Select Style (from commit 7571019)
export const getOriginalStatusSelectStyle = () => ({
  fontSize: '12px',
  fontWeight: 500,
  backgroundColor: 'transparent',
  border: '0',
  outline: 'none',
  cursor: 'pointer',
  width: '100%',
  appearance: 'none' as const,
});

// Original Assignee Container Style (from commit 7571019)  
export const getOriginalAssigneeContainerStyle = () => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '12px',
  color: '#4B5563', // text-gray-600
});

// Semantic Color Functions - Direct Access to Commonly Used Colors
export const getSemanticColors = () => ({
  // Primary Brand Colors
  primary: DesignSystem.colors.brand[600],           // Main brand color #4f46e5
  primaryHover: DesignSystem.colors.brand[700],      // Hover state #4338ca
  primaryActive: DesignSystem.colors.brand[800],     // Active/pressed state #3730a3
  primaryLight: DesignSystem.colors.brand[50],       // Light background #eef2ff
  primaryMuted: DesignSystem.colors.brand[400],      // Muted/disabled state #818cf8
  
  // Success Colors
  success: DesignSystem.colors.success[500],         // Success actions #10b981
  successHover: DesignSystem.colors.success[600],    // Success hover #059669
  successLight: DesignSystem.colors.success[50],     // Success background #ecfdf5
  
  // Error Colors  
  error: DesignSystem.colors.error[500],             // Error actions #ef4444
  errorHover: DesignSystem.colors.error[600],        // Error hover #dc2626
  errorLight: DesignSystem.colors.error[50],         // Error background #fef2f2
  
  // Warning Colors
  warning: DesignSystem.colors.warning[500],         // Warning actions #f59e0b
  warningHover: DesignSystem.colors.warning[600],    // Warning hover #d97706
  warningLight: DesignSystem.colors.warning[50],     // Warning background #fffbeb
  
  // Neutral/Gray Colors
  textPrimary: DesignSystem.colors.gray[800],        // Main text #1f2937
  textSecondary: DesignSystem.colors.gray[600],      // Secondary text #4b5563
  textMuted: DesignSystem.colors.gray[400],          // Muted text #9ca3af
  border: DesignSystem.colors.gray[200],             // Default borders #e5e7eb
  borderLight: DesignSystem.colors.gray[100],        // Light borders #f3f4f6
  background: DesignSystem.colors.gray[50],          // Light backgrounds #f9fafb
});

// Quick Color Access Functions
export const getBrandColor = (shade: number) => DesignSystem.colors.brand[shade as keyof typeof DesignSystem.colors.brand];
export const getGrayColor = (shade: number) => DesignSystem.colors.gray[shade as keyof typeof DesignSystem.colors.gray];
export const getSuccessColor = (shade: number) => DesignSystem.colors.success[shade as keyof typeof DesignSystem.colors.success];
export const getErrorColor = (shade: number) => DesignSystem.colors.error[shade as keyof typeof DesignSystem.colors.error];
export const getWarningColor = (shade: number) => DesignSystem.colors.warning[shade as keyof typeof DesignSystem.colors.warning];

// Badge helper functions
export const getBadgeStyle = (type: 'dev' | 'needReview' | 'inProgress' | 'completed' | 'error') => {
  const badge = DesignSystem.badges[type];
  return {
    backgroundColor: badge.background,
    color: badge.text,
    border: `1px solid ${badge.border}`,
    borderRadius: DesignSystem.borderRadius.md,
    padding: '2px 6px',
    fontSize: '12px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
  };
};

export const getBadgeTailwindClasses = (type: 'dev' | 'needReview' | 'inProgress' | 'completed' | 'error') => {
  switch (type) {
    case 'dev':
      return 'text-xs font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700';
    case 'needReview':
      return 'text-xs font-medium px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700';
    case 'inProgress':
      return 'text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700';
    case 'completed':
      return 'text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700';
    case 'error':
      return 'text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700';
    default:
      return 'text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700';
  }
};

// Z-Index Helper Functions
export const getZIndex = (layer: keyof typeof DesignSystem.zIndex) => {
  return DesignSystem.zIndex[layer];
};

// Get z-index with Tailwind class
export const getZIndexClass = (layer: keyof typeof DesignSystem.zIndex) => {
  const value = DesignSystem.zIndex[layer];
  return `z-[${value}]`;
};

// Get z-index with inline style
export const getZIndexStyle = (layer: keyof typeof DesignSystem.zIndex) => {
  return { zIndex: DesignSystem.zIndex[layer] };
};

// Z-Index comparison helper
export const compareZIndex = (layer1: keyof typeof DesignSystem.zIndex, layer2: keyof typeof DesignSystem.zIndex) => {
  return DesignSystem.zIndex[layer1] - DesignSystem.zIndex[layer2];
};

// Get appropriate z-index for component type
export const getComponentZIndex = (componentType: 'modal' | 'dropdown' | 'tooltip' | 'notification' | 'sticky' | 'overlay') => {
  switch (componentType) {
    case 'modal':
      return DesignSystem.zIndex.modalCritical;
    case 'dropdown':
      return DesignSystem.zIndex.dropdown;
    case 'tooltip':
      return DesignSystem.zIndex.popover;
    case 'notification':
      return DesignSystem.zIndex.notification;
    case 'sticky':
      return DesignSystem.zIndex.sticky;
    case 'overlay':
      return DesignSystem.zIndex.overlay;
    default:
      return DesignSystem.zIndex.content;
  }
};

// Usage Examples:
// const colors = getSemanticColors();
// backgroundColor: colors.primary           // #4f46e5
// color: colors.textPrimary                // #1f2937
// borderColor: colors.border               // #e5e7eb
// 
// Z-Index Examples:
// zIndex: getZIndex('modal')               // 5000
// className: getZIndexClass('modalCritical') // 'z-[50000]'
// style: getZIndexStyle('dropdown')        // { zIndex: 100 }
// 
// Or direct access:
// backgroundColor: getBrandColor(600)      // #4f46e5
// color: getGrayColor(800)                 // #1f2937